import React, {useEffect, useMemo, useState} from "react";
import {
    Alert,
    Button,
    Card,
    Col,
    Empty,
    Form,
    Grid,
    Input,
    InputNumber,
    Modal,
    Pagination,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from "antd";
import {
    DeleteOutlined,
    EditOutlined,
    InfoCircleOutlined,
    KeyOutlined,
    PlusOutlined,
    TeamOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import {
    useCreateOperator,
    useDeleteOperator,
    useOperators,
    useSetOperatorPassword,
    useUpdateOperator,
} from "./operators-query";
import SupervisorsSection from "./supervisors";
import {useSaveSchedule} from "../shifts/shifts-query";
import {useSubscription} from "react-stomp-hooks";
import {useQueryClient} from "@tanstack/react-query";

const {Text} = Typography;
const {useBreakpoint} = Grid;

// کمکی برای اعداد فارسی
const enToFaDigits = (v) => String(v ?? "").replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

// روزهای هفته
const DAYS = [
    {value: "SATURDAY", label: "شنبه"},
    {value: "SUNDAY", label: "یکشنبه"},
    {value: "MONDAY", label: "دوشنبه"},
    {value: "TUESDAY", label: "سه‌شنبه"},
    {value: "WEDNESDAY", label: "چهارشنبه"},
    {value: "THURSDAY", label: "پنجشنبه"},
    {value: "FRIDAY", label: "جمعه"},
];

const findDayLabel = (value) => DAYS.find((d) => d.value === value)?.label || value;

// تبدیل 08:00 → ۰۸:۰۰
const formatTime = (h, m) => {
    const hh = String(h ?? 0).padStart(2, "0");
    const mm = String(m ?? 0).padStart(2, "0");
    return enToFaDigits(`${hh}:${mm}`);
};

// نمایش خلاصه برنامه کاری برای جدول
const formatScheduleSummary = (schedule = []) => {
    if (!schedule.length) return "تعریف نشده";
    return schedule
        .map((s) => {
            const from = formatTime(s.startHour, s.startMinute);
            const to = formatTime(s.endHour, s.endMinute);
            return `${findDayLabel(s.day)} ${from} تا ${to}`;
        })
        .join("، ");
};

const Operators = () => {
    const queryClient = useQueryClient();
    const screens = useBreakpoint();
    const isDesktop = !!screens.md;

    // pagination سروری
    const [pagination, setPagination] = useState({current: 1, pageSize: 10});
    const filters = {}; // فعلاً بدون فیلتر
    const [editOpen, setEditOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const [showOpen, setShowOpen] = useState(false);
    const [showRecord, setShowRecord] = useState(null);

    const [passwordOpen, setPasswordOpen] = useState(false);
    const [passwordTarget, setPasswordTarget] = useState(null);


    const {data, isLoading, refetch} =
        useOperators(pagination.current - 1, pagination.pageSize, filters);

    /*
     * A supervisor edits the same rota from the shift screen, so this table has to hear about it.
     * Without this, an admin with this page open would keep showing last week's hours and would
     * overwrite the supervisor's change the next time they saved an operator from here.
     *
     * A literal per-user destination — Spring routes it to this session only, and the server pushes
     * it solely to principals the oversight check already accepted. Never a computed or nullable
     * destination: useSubscription wraps a non-array argument as [value], so a null once subscribed
     * the client to the literal string "null" and the refused SUBSCRIBE tore down the session.
     */
    useSubscription("/user/queue/operator/schedule", () => {
        queryClient.invalidateQueries({queryKey: ["operators"]});
        queryClient.invalidateQueries({queryKey: ["operator-schedules"]});
    });

    const operators = useMemo(
        () => data?.data?.content || data?.content || [],
        [data]
    );

    const total =
        data?.data?.page?.totalElements ??
        data?.page?.totalElements ??
        operators.length;


    const {mutate: deleteOperator} = useDeleteOperator();

    const openEdit = (row = null) => {
        setEditing(row);
        setEditOpen(true);
    };

    const openShow = (row) => {
        setShowRecord(row);
        setShowOpen(true);
    };

    const openPassword = (row) => {
        setPasswordTarget(row);
        setPasswordOpen(true);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: "حذف اپراتور",
            content: "آیا از حذف این اپراتور مطمئن هستید؟",
            okText: "بله، حذف شود",
            cancelText: "انصراف",
            okButtonProps: {danger: true},
            onOk: () => {
                const isLastItemOnPage =
                    operators.length === 1 && pagination.current > 1;

                if (isLastItemOnPage) {
                    setPagination((prev) => ({
                        ...prev,
                        current: prev.current - 1,
                    }));
                }
                deleteOperator(id);
            },
        });
    };

    const handleSaved = () => {
        // بعد از ایجاد/ویرایش موفق، فقط مودال رو ببند
        // refetch توسط useCreateOperator/useUpdateOperator انجام می‌شه (invalidateQueries)
        setEditOpen(false);
        setEditing(null);
    };

    // ---------- ستون‌های جدول ----------
    const columns = [
        {
            title: "نام و نام خانوادگی",
            dataIndex: "fullName",
            width: 200,
            /*
             * A profile and a login are separate rows, and an operator can have the first without
             * the second — a name, an email, a shift, and no way in. That used to look exactly like
             * a working operator here, which is how one kept the evening shift for weeks while her
             * conversations went unanswered. The rota now skips such an operator; this says so, and
             * the key button beside it is the repair.
             */
            render: (v, r) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{v}</Text>
                    {r.canSignIn === false && (
                        <Tooltip title="این اپراتور حساب ورود ندارد و نمی‌تواند وارد شود. چتی هم به او سپرده نمی‌شود. برای ساختن حساب، از دکمهٔ «تغییر رمز عبور» یک رمز برایش تعیین کنید.">
                            <Tag color="error" icon={<WarningOutlined/>} style={{fontSize: 11, marginTop: 2}}>
                                بدون حساب ورود
                            </Tag>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: "ایمیل",
            dataIndex: "email",
            width: 220,
            ellipsis: true,
        },
        {
            title: "برنامه کاری",
            dataIndex: "workSchedule",
            ellipsis: true,
            render: (schedule) => (
                <Tooltip title={formatScheduleSummary(schedule)}>
                    <span>{formatScheduleSummary(schedule)}</span>
                </Tooltip>
            ),
        },
        {
            title: "عملیات",
            key: "actions",
            width: 180,
            align: "center",
            render: (_, r) => (
                <Space>
                    <Tooltip title="مشاهده">
                        <Button
                            size="small"
                            icon={<InfoCircleOutlined/>}
                            onClick={() => openShow(r)}
                        />
                    </Tooltip>
                    <Tooltip title="ویرایش">
                        <Button
                            size="small"
                            icon={<EditOutlined/>}
                            onClick={() => openEdit(r)}
                        />
                    </Tooltip>
                    <Tooltip title={r.canSignIn === false ? "ساختن حساب ورود" : "تغییر رمز عبور"}>
                        <Button
                            size="small"
                            type={r.canSignIn === false ? "primary" : "default"}
                            icon={<KeyOutlined/>}
                            onClick={() => openPassword(r)}
                        />
                    </Tooltip>
                    <Tooltip title="حذف">
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined/>}
                            onClick={() => handleDelete(r.id)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const renderMobileItem = (r) => (
        <Card
            key={r.id}
            size="small"
            className="mb-2 rounded-xl"
            bodyStyle={{padding: 12}}
        >
            <div className="flex justify-between gap-2 mb-1">
                <Text strong>{r.fullName}</Text>
                <Tag color="blue">اپراتور</Tag>
            </div>
            <div className="text-xs text-gray-500 break-all mb-2">{r.email}</div>
            <div className="text-xs mb-2">
                <Text type="secondary">برنامه کاری: </Text>
                <span>{formatScheduleSummary(r.workSchedule)}</span>
            </div>
            <Space size="small" wrap>
                <Button
                    size="small"
                    icon={<InfoCircleOutlined/>}
                    onClick={() => openShow(r)}
                >
                    مشاهده
                </Button>
                <Button
                    size="small"
                    icon={<EditOutlined/>}
                    onClick={() => openEdit(r)}
                >
                    ویرایش
                </Button>
                <Button
                    size="small"
                    icon={<KeyOutlined/>}
                    onClick={() => openPassword(r)}
                >
                    رمز عبور
                </Button>
                <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined/>}
                    onClick={() => handleDelete(r.id)}
                >
                    حذف
                </Button>
            </Space>
        </Card>
    );


    return (
        <Card
            dir="rtl"
            className="w-full"
            title={
                <Space>
                    <TeamOutlined/>
                    <span>مدیریت اپراتورها</span>
                </Space>
            }
            extra={
                <Space size="small" wrap>
                    <Button
                        type="primary"
                        icon={<PlusOutlined/>}
                        onClick={() => openEdit(null)}
                    >
                        افزودن اپراتور
                    </Button>
                </Space>
            }
            bodyStyle={{padding: 12}}
        >
            {isDesktop ? (
                <Table
                    rowKey="id"
                    size="small"
                    bordered
                    loading={isLoading}
                    dataSource={operators}
                    columns={columns}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="اپراتوری یافت نشد"
                            />
                        ),
                    }}
                    pagination={false}
                    rowClassName={(_, idx) => (idx % 2 === 0 ? "zebra-row" : "")}
                    scroll={{x: 800}}
                />
            ) : (
                <>
                    {operators.length ? (
                        operators.map((r) => renderMobileItem(r))
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="اپراتوری یافت نشد"
                        />
                    )}
                </>
            )}

            {total > 0 && (
                <div
                    style={{
                        marginTop: 12,
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={total}
                        showSizeChanger
                        pageSizeOptions={["5", "10", "20", "50"]}
                        locale={{items_per_page: ""}}
                        onChange={(current, pageSize) =>
                            setPagination({current, pageSize})
                        }
                    />
                </div>
            )}

            <style>{`
      .zebra-row td { background: var(--npe-row-alt); }
    `}</style>

            {/* Modals همون قبلی */}
            <EditOperatorModal
                open={editOpen}
                onClose={() => {
                    setEditOpen(false);
                    setEditing(null);
                }}
                initial={editing}
                onSaved={handleSaved}
            />

            <ShowOperatorModal
                open={showOpen}
                onClose={() => {
                    setShowOpen(false);
                    setShowRecord(null);
                }}
                record={showRecord}
            />

            <SetPasswordModal
                open={passwordOpen}
                onClose={() => {
                    setPasswordOpen(false);
                    setPasswordTarget(null);
                }}
                operator={passwordTarget}
            />

            {/* Supervisor accounts live below the operator table rather than inside it: a supervisor
                has no shift and no caseload, so every column above would be blank for them. */}
            <SupervisorsSection/>
        </Card>
    );
};

export default Operators;

/* ------------------ Modal تغییر رمز عبور ------------------ */
/*
 * An admin setting an operator's password, without needing the old one — which is the whole point,
 * since the usual reason to open this is that nobody knows the old one.
 *
 * It also exists to close something that was true of every account created before today: the backend
 * used the operator's own email address as their initial password, and the console never offered a
 * way to set anything else.
 */
function SetPasswordModal({open, onClose, operator}) {
    const [form] = Form.useForm();
    const {mutate: setPassword, isPending} = useSetOperatorPassword(() => {
        message.success("رمز عبور تغییر کرد.");
        form.resetFields();
        onClose();
    });

    useEffect(() => {
        if (open) form.resetFields();
    }, [open, operator]); // eslint-disable-line

    const handleFinish = (vals) => {
        setPassword(
            {id: operator?.id, newPassword: vals.newPassword},
            {
                onError: (err) => {
                    // The backend's Persian reason is the useful one ("at least 8 characters",
                    // "cannot be the email"); fall back only when there isn't one.
                    const reason =
                        err?.response?.data?.message ||
                        err?.response?.data?.error ||
                        "تغییر رمز عبور ناموفق بود.";
                    message.error(reason);
                },
            }
        );
    };

    return (
        <Modal
            open={open}
            title="تغییر رمز عبور اپراتور"
            onCancel={onClose}
            onOk={() => form.submit()}
            okText="ذخیره رمز جدید"
            cancelText="انصراف"
            destroyOnClose
            confirmLoading={isPending}
        >
            <Alert
                type="info"
                showIcon
                style={{marginBottom: 16}}
                message={operator?.fullName || operator?.email}
                description="رمز جدید را از راهی امن به اپراتور بدهید و از او بخواهید پس از ورود آن را تغییر دهد. این رمز در هیچ جای پنل دوباره نمایش داده نمی‌شود."
            />
            <Form form={form} layout="vertical" requiredMark={false} onFinish={handleFinish}>
                <Form.Item
                    name="newPassword"
                    label="رمز عبور جدید"
                    rules={[
                        {required: true, message: "رمز عبور جدید را وارد کنید"},
                        {min: 8, message: "حداقل ۸ کاراکتر"},
                        () => ({
                            validator(_, value) {
                                const email = (operator?.email || "").trim().toLowerCase();
                                const local = email.includes("@") ? email.split("@")[0] : email;
                                const v = (value || "").trim().toLowerCase();
                                if (v && email && (v === email || v === local)) {
                                    return Promise.reject(new Error("رمز عبور نمی‌تواند همان ایمیل باشد"));
                                }
                                return Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <Input.Password autoComplete="new-password" autoFocus/>
                </Form.Item>

                <Form.Item
                    name="confirmPassword"
                    label="تکرار رمز عبور جدید"
                    dependencies={["newPassword"]}
                    rules={[
                        {required: true, message: "رمز عبور را دوباره وارد کنید"},
                        ({getFieldValue}) => ({
                            validator(_, value) {
                                if (!value || getFieldValue("newPassword") === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error("دو رمز یکسان نیستند"));
                            },
                        }),
                    ]}
                >
                    <Input.Password autoComplete="new-password"/>
                </Form.Item>
            </Form>
        </Modal>
    );
}

/* ------------------ Modal نمایش جزئیات ------------------ */
function ShowOperatorModal({open, onClose, record}) {
    if (!record) return null;

    return (
        <Modal
            open={open}
            title="جزئیات اپراتور"
            onCancel={onClose}
            footer={<Button onClick={onClose}>بستن</Button>}
            destroyOnClose
            width={600}
        >
            <div dir="rtl">
                {/* ردیف اول: نام + ایمیل کنار هم */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 24,
                        marginBottom: 16,
                        flexWrap: "wrap",
                    }}
                >
                    <div style={{minWidth: 200}}>
                        <Text type="secondary" style={{display: "block", marginBottom: 4}}>
                            نام و نام خانوادگی
                        </Text>
                        <Text strong>{record.fullName}</Text>
                    </div>

                    <div style={{minWidth: 200}}>
                        <Text type="secondary" style={{display: "block", marginBottom: 4}}>
                            ایمیل
                        </Text>
                        <Text>
                            <span dir="ltr" style={{display: "inline-block"}}>
                                {record.email}
                            </span>
                        </Text>
                    </div>
                </div>

                {/* برنامه کاری زیر اطلاعات اصلی */}
                <div>
                    <Text type="secondary" style={{display: "block", marginBottom: 8}}>
                        برنامه کاری
                    </Text>

                    {record.workSchedule?.length ? (
                        <div style={{display: "flex", flexDirection: "column", gap: 6}}>
                            {record.workSchedule.map((s, idx) => (
                                <div
                                    key={idx}
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        borderRadius: 8,
                                        padding: "6px 10px",
                                        border: "1px solid var(--npe-border)",
                                        background: "var(--npe-surface-sunken)",
                                        fontSize: 12,
                                    }}
                                >
                                    <span>{findDayLabel(s.day)}</span>
                                    <span>
                                        {formatTime(s.startHour, s.startMinute)} تا{" "}
                                        {formatTime(s.endHour, s.endMinute)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <Text>تعریف نشده</Text>
                    )}
                </div>
            </div>
        </Modal>
    );
}

/* ------------------ Modal ساخت/ویرایش ------------------ */
function EditOperatorModal({open, onClose, initial, onSaved}) {
    const [form] = Form.useForm();
    const isEdit = !!initial;

    // React Query mutations.
    //
    // `isPending`, not `isLoading`: this is react-query v5, where useMutation dropped isLoading.
    // Destructuring the old name yielded undefined, so confirmLoading never engaged and the save
    // button stayed live through the whole request — clickable twice.
    const {mutate: createOperatorMut, isPending: creating} = useCreateOperator(onSaved);
    const {mutate: updateOperatorMut, isPending: updating} = useUpdateOperator();
    // The rota goes through its own endpoint even from this form — see handleFinish. onSaved runs
    // when THAT lands, so the modal does not close while the schedule is still in flight.
    const {mutate: saveScheduleMut, isPending: savingSchedule} = useSaveSchedule(onSaved);
    const loading = creating || updating || savingSchedule;

    /*
     * Show the admin why a save failed.
     *
     * Nothing here reported failure at all before: the mutations had no onError, the QueryClient has
     * no default one, and the axios interceptor only logs. A refused save left the modal open and
     * silent, so the natural next move was to press the button again. That was survivable when the
     * only likely failure was a duplicate email; it is not now that the form can be refused for a
     * weak password — the backend writes a precise Persian reason and the admin was never shown it.
     */
    const reportFailure = (err) => {
        const reason =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            (err?.response?.status === 500
                ? "ذخیره ناموفق بود. اگر این ایمیل قبلاً ثبت شده، ایمیل دیگری وارد کنید."
                : "ذخیره ناموفق بود.");
        message.error(reason);
    };

    useEffect(() => {
        if (initial) {
            form.setFieldsValue({
                email: initial.email,
                fullName: initial.fullName,
                workSchedule: initial.workSchedule?.length
                    ? initial.workSchedule
                    : [{day: "SATURDAY", startHour: 8, startMinute: 0, endHour: 12, endMinute: 0}],
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                workSchedule: [
                    {day: "SATURDAY", startHour: 8, startMinute: 0, endHour: 12, endMinute: 0},
                ],
            });
        }
    }, [initial, open]); // eslint-disable-line

    const handleFinish = (vals) => {
        const payload = {
            id: initial?.id, // برای create بک‌اند خودش id می‌سازد
            email: vals.email,
            fullName: vals.fullName,
            // Only ever sent on create. The backend ignores it on update, and changing an existing
            // operator's password is a separate admin-only endpoint.
            ...(isEdit ? {} : {initialPassword: vals.initialPassword}),
            workSchedule: (vals.workSchedule || []).map((s) => ({
                day: s.day,
                startHour: s.startHour ?? 0,
                startMinute: s.startMinute ?? 0,
                endHour: s.endHour ?? 0,
                endMinute: s.endMinute ?? 0,
            })),
        };

        if (isEdit) {
            /*
             * Two calls, because the rota has exactly one write path now.
             *
             * /operator/update no longer accepts a schedule from anybody — it silently dropped the
             * field for admins too, which meant this form could store a shift that ends before it
             * starts: legal for the column, matched by no assignment query, invisible on every
             * screen. That is the failure that leaves an operator looking staffed while customers
             * wait. The schedule endpoint validates, records who changed it, and tells other open
             * consoles; sending it here keeps this form working and puts it behind those.
             */
            const {workSchedule, ...profile} = payload;
            updateOperatorMut(profile, {
                onError: reportFailure,
                onSuccess: () =>
                    saveScheduleMut(
                        {operatorId: initial.id, workSchedule},
                        {onError: reportFailure}
                    ),
            });
        } else {
            createOperatorMut(payload, {onError: reportFailure});
        }
    };

    return (
        <Modal
            open={open}
            title={isEdit ? "ویرایش اپراتور" : "افزودن اپراتور جدید"}
            onCancel={onClose}
            onOk={() => form.submit()}
            okText="ذخیره"
            cancelText="انصراف"
            destroyOnClose
            width={780}
            confirmLoading={loading}
        >
            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                onFinish={handleFinish}
            >
                <Row gutter={12}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="fullName"
                            label="نام و نام خانوادگی"
                            rules={[{required: true, message: "نام را وارد کنید"}]}
                        >
                            <Input/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="email"
                            label="ایمیل"
                            rules={[
                                {required: true, message: "ایمیل را وارد کنید"},
                                {type: "email", message: "ایمیل نامعتبر است"},
                            ]}
                        >
                            <Input/>
                        </Form.Item>
                    </Col>
                </Row>

                {/* Only on create. Until now the console never asked, and the backend quietly used
                    the operator's own email address as their password — an address printed in the
                    operator list and in every chat header. Editing an existing operator does not
                    show this: changing a password is the separate «تغییر رمز» action, so that a
                    routine name or schedule edit can never silently reset someone's sign-in. */}
                {!isEdit && (
                    <Row gutter={12}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="initialPassword"
                                label="رمز عبور اولیه"
                                tooltip="اپراتور با این رمز وارد پنل می‌شود. آن را از راهی امن به او بدهید و از او بخواهید تغییرش دهد."
                                rules={[
                                    {required: true, message: "رمز عبور اولیه را وارد کنید"},
                                    {min: 8, message: "حداقل ۸ کاراکتر"},
                                    ({getFieldValue}) => ({
                                        validator(_, value) {
                                            const email = (getFieldValue("email") || "").trim().toLowerCase();
                                            const local = email.includes("@") ? email.split("@")[0] : email;
                                            const v = (value || "").trim().toLowerCase();
                                            if (v && email && (v === email || v === local)) {
                                                return Promise.reject(
                                                    new Error("رمز عبور نمی‌تواند همان ایمیل باشد")
                                                );
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password autoComplete="new-password"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="confirmPassword"
                                label="تکرار رمز عبور"
                                dependencies={["initialPassword"]}
                                rules={[
                                    {required: true, message: "رمز عبور را دوباره وارد کنید"},
                                    ({getFieldValue}) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue("initialPassword") === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error("دو رمز یکسان نیستند"));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password autoComplete="new-password"/>
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                <Form.Item
                    label="برنامه کاری (روز و ساعت)"
                    tooltip="روز را انتخاب کنید و ساعت شروع/پایان را با عدد وارد کنید"
                >
                    <Form.List name="workSchedule">
                        {(fields, {add, remove}) => (
                            <div className="space-y-2">
                                {fields.map((field, index) => (
                                    <Card
                                        key={field.key}
                                        size="small"
                                        className="border border-gray-200 rounded-xl"
                                        bodyStyle={{padding: 12}}
                                    >
                                        {/* هدر هر بازه: عنوان + دکمه حذف در یک ردیف مرتب */}
                                        <div className="flex items-center justify-between mb-2">
                                            <Text type="secondary" className="text-xs">
                                                بازه کاری {fields.length > 1 ? `#${index + 1}` : ""}
                                            </Text>
                                            {fields.length > 1 && (
                                                <Button
                                                    danger
                                                    type="text"
                                                    size="small"
                                                    icon={<DeleteOutlined/>}
                                                    onClick={() => remove(field.name)}
                                                >
                                                    حذف
                                                </Button>
                                            )}
                                        </div>

                                        <Row gutter={8} align="middle">
                                            <Col xs={24} md={6}>
                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, "day"]}
                                                    fieldKey={[field.fieldKey, "day"]}
                                                    label="روز"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "روز را انتخاب کنید",
                                                        },
                                                    ]}
                                                >
                                                    <Select
                                                        options={DAYS}
                                                        placeholder="انتخاب روز"
                                                        dropdownMatchSelectWidth={false}
                                                        getPopupContainer={(n) =>
                                                            n.parentElement
                                                        }
                                                    />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={12} md={4}>
                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, "startHour"]}
                                                    fieldKey={[field.fieldKey, "startHour"]}
                                                    label="ساعت شروع"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "ساعت شروع را وارد کنید",
                                                        },
                                                    ]}
                                                >
                                                    <InputNumber
                                                        min={0}
                                                        max={23}
                                                        className="w-full"
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={12} md={4}>
                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, "startMinute"]}
                                                    fieldKey={[field.fieldKey, "startMinute"]}
                                                    label="دقیقه شروع"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "دقیقه شروع را وارد کنید",
                                                        },
                                                    ]}
                                                >
                                                    <InputNumber
                                                        min={0}
                                                        max={59}
                                                        className="w-full"
                                                    />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={12} md={4}>
                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, "endHour"]}
                                                    fieldKey={[field.fieldKey, "endHour"]}
                                                    label="ساعت پایان"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "ساعت پایان را وارد کنید",
                                                        },
                                                    ]}
                                                >
                                                    <InputNumber
                                                        min={0}
                                                        max={23}
                                                        className="w-full"
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={12} md={4}>
                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, "endMinute"]}
                                                    fieldKey={[field.fieldKey, "endMinute"]}
                                                    label="دقیقه پایان"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "دقیقه پایان را وارد کنید",
                                                        },
                                                    ]}
                                                >
                                                    <InputNumber
                                                        min={0}
                                                        max={59}
                                                        className="w-full"
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Card>
                                ))}

                                <Button
                                    type="dashed"
                                    block
                                    icon={<PlusOutlined/>}
                                    onClick={() =>
                                        add({
                                            day: "SATURDAY",
                                            startHour: 8,
                                            startMinute: 0,
                                            endHour: 12,
                                            endMinute: 0,
                                        })
                                    }
                                >
                                    افزودن بازه‌ی کاری
                                </Button>
                            </div>
                        )}
                    </Form.List>
                </Form.Item>
            </Form>
        </Modal>
    );
}
